import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { projectService } from '@services/projectService';
import { CACHE_DURATION } from '../utils/cache';
import type { RootState } from '../index';

// ── Types ──

export type ProjectStatus =
  | 'Lead'
  | 'Inquiry'
  | 'Booked'
  | 'In Progress'
  | 'Event Day'
  | 'Post-Production'
  | 'Completed'
  | 'Archived';
export type ContractStatus = 'Not sent' | 'Sent' | 'Signed' | 'Expired';
export type WorkflowStatus = 'Not started' | 'In Progress' | 'Paused' | 'Complete';

export interface Project {
  id: string;
  clientId?: string | null;
  name: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string | null;
  status: ProjectStatus;
  contractStatus: ContractStatus;
  contractName?: string | null;
  contractCountersigned?: boolean;
  workflowStatus: WorkflowStatus;
  amountPaid: number | null;
  invoiceTotal?: number | null;
  projectDate: string | null;
  projectEndDate?: string | null;
  eventType?: string | null;
  showAsBusy?: boolean;
  location?: string | null;
  notes?: string | null;
  assignedTo: string | null;
  assignedMemberIds?: string[];
  accessRole?: 'owner' | 'member';
  tags: string[];
  projectSource: string | null;
  customFields?: Record<string, string>;
  automationTemplateId?: string | null;
  deletedAt?: string | null;
  scheduledDeletionAt?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ProjectState {
  projects: Project[];
  selectedProjectId: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: ProjectState = {
  projects: [],
  selectedProjectId: null,
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

// ── Thunks ──

export const fetchProjects = createAsyncThunk(
  'projects/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { lastFetched } = state.projects;
    if (!options?.force && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
      return null; // cached
    }
    try {
      return await projectService.getProjects({ includeDeleted: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load projects';
      return rejectWithValue(msg);
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/create',
  async (data: Partial<Project>, { rejectWithValue }) => {
    try {
      return await projectService.createProject(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create project';
      return rejectWithValue(msg);
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/update',
  async ({ id, data }: { id: string; data: Partial<Project> }, { rejectWithValue }) => {
    try {
      return await projectService.updateProject(id, data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update project';
      return rejectWithValue(msg);
    }
  }
);

export const updateProjectAssignments = createAsyncThunk(
  'projects/updateAssignments',
  async ({ id, memberIds }: { id: string; memberIds: string[] }, { rejectWithValue }) => {
    try {
      return await projectService.updateAssignments(id, memberIds);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update project assignments';
      return rejectWithValue(msg);
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      return await projectService.deleteProject(id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete project';
      return rejectWithValue(msg);
    }
  }
);

export const restoreProject = createAsyncThunk(
  'projects/restore',
  async (id: string, { rejectWithValue }) => {
    try {
      return await projectService.restoreProject(id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to restore project';
      return rejectWithValue(msg);
    }
  }
);

export const permanentlyDeleteProject = createAsyncThunk(
  'projects/permanentlyDelete',
  async (id: string, { rejectWithValue }) => {
    try {
      await projectService.permanentlyDeleteProject(id);
      return id;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to permanently delete project';
      return rejectWithValue(msg);
    }
  }
);

// ── Slice ──

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setSelectedProject(state, action: PayloadAction<string | null>) {
      state.selectedProjectId = action.payload;
    },
    clearProjectError(state) {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProjects.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload !== null) {
          state.projects = action.payload;
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createProject.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.isSaving = false;
        state.projects.unshift(action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(updateProject.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.isSaving = false;
        const idx = state.projects.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) state.projects[idx] = action.payload;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(updateProjectAssignments.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateProjectAssignments.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) state.projects[index] = action.payload;
      })
      .addCase(updateProjectAssignments.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(deleteProject.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.isSaving = false;
        const idx = state.projects.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) {
          state.projects[idx] = action.payload;
        }
        if (state.selectedProjectId === action.payload.id) state.selectedProjectId = null;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(restoreProject.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(restoreProject.fulfilled, (state, action) => {
        state.isSaving = false;
        const idx = state.projects.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) state.projects[idx] = action.payload;
      })
      .addCase(restoreProject.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(permanentlyDeleteProject.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(permanentlyDeleteProject.fulfilled, (state, action) => {
        state.isSaving = false;
        state.projects = state.projects.filter(p => p.id !== action.payload);
        if (state.selectedProjectId === action.payload) state.selectedProjectId = null;
      })
      .addCase(permanentlyDeleteProject.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedProject, clearProjectError } = projectSlice.actions;

// ── Selectors ──

export const selectProjects = (state: RootState) => state.projects.projects;
export const selectProjectsLoading = (state: RootState) => state.projects.isLoading;
export const selectProjectsSaving = (state: RootState) => state.projects.isSaving;
export const selectProjectsError = (state: RootState) => state.projects.error;
export const selectSelectedProjectId = (state: RootState) => state.projects.selectedProjectId;

export default projectSlice.reducer;
